import type { CompRef } from '@wix/document-services-types';
import { ManifestComponentExtensionType, ManifestStore } from '../../stores';
import { AbstractExternalAPIHandler } from '../AbstractExternalAPIHandler';
import { ExternalOOIStylesHandler } from './ExternalOOIStylesHandler';
import {
  ChangeType,
  IManifestChangeDetection,
} from '../../ManifestChangeDetection';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { DependencyToken, DependenciesContributor } from '../../dependencies';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

const stylesOverrides: Record<string, string> = {
  inlineBlock: 'inline-block',
  inlineTable: 'inline-table',
  inlineFlex: 'inline-flex',
  inlineGrid: 'inline-grid',
  listItem: 'list-item',
  dropShadow: 'drop-shadow',
  hueRotate: 'hue-rotate',

  // TODO: remove deprecated definitions after merging https://github.com/wix-private/devcenter/pull/13861/files
  // and deprecated values are no longer used in manifest
  inline_block: 'inline-block',
  inline_flex: 'inline-flex',
  inline_table: 'inline-table',
  inline_grid: 'inline-grid',
  list_item: 'list-item',
  drop_shadow: 'drop-shadow',
  hue_rotate: 'hue-rotate',
};

export class ExternalStylesHandler extends AbstractExternalAPIHandler {
  private externalOOIStylesHandler: ExternalOOIStylesHandler;

  constructor(
    dependenciesContributor: DependenciesContributor,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestStore: ManifestStore,
    manifestChangeDetection: IManifestChangeDetection,
    manifestServiceFlags: ManifestServiceFlags,
    manifestServiceStats: ManifestServiceStats,
  ) {
    super(
      dependenciesContributor,
      editorPointerAPI,
      manifestStore,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );

    this.externalOOIStylesHandler = new ExternalOOIStylesHandler(
      dependenciesContributor,
      editorPointerAPI,
      manifestStore,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );
  }

  getStyles(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    options?: {
      customGetDefaultStyle: () => {
        cssProperties: Record<string, string>;
        cssCustomProperties: Record<string, string>;
      };
    },
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER: {
        const getStyles = this.dependenciesContributor.get(
          DependencyToken.BUILDER_STYLES_GET,
        );

        return getStyles(editorPointer, options);
      }

      case ManifestComponentExtensionType.OOI: {
        return this.externalOOIStylesHandler.getStyles(editorPointer, options);
      }

      default: {
        const getStyles = this.dependenciesContributor.get(
          DependencyToken.UNKNOWN_COMPONENT_STYLES_GET,
        );

        return getStyles(editorPointer, options);
      }
    }
  }

  private setStylesInternal(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    styles: {
      cssProperties: Record<string, string>;
      cssCustomProperties: Record<string, string>;
    },
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER: {
        const _styles = Object.entries(styles).reduce(
          (acc, [key, value]) =>
            value && Object.keys(value)?.length > 0
              ? { ...acc, [key]: resolveNonCssLikeValues(value) }
              : acc,
          {},
        );

        const updateStyles = this.dependenciesContributor.get(
          DependencyToken.BUILDER_STYLES_UPDATE,
        );
        updateStyles(editorPointer, _styles);
        break;
      }

      case ManifestComponentExtensionType.OOI: {
        this.externalOOIStylesHandler.setStyles(editorPointer, styles);
        break;
      }

      default: {
        const setStyles = this.dependenciesContributor.get(
          DependencyToken.UNKNOWN_COMPONENT_STYLES_UPDATE,
        );

        return setStyles(editorPointer, styles);
      }
    }

    this.manifestChangeDetection.notify({
      type: ChangeType.Styles,
      editorPointer,
      extensionType: type,
      value: {
        cssProperties: styles.cssProperties,
        cssCustomProperties: styles.cssCustomProperties,
      },
    });

    return;
  }

  setStyles(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    styles: {
      cssProperties: Record<string, string>;
      cssCustomProperties: Record<string, string>;
    },
  ) {
    const hasSetStylesRouter = this.dependenciesContributor.has(
      DependencyToken.STYLES_UPDATE_ROUTER,
    );

    if (hasSetStylesRouter) {
      const setStylesRouter = this.dependenciesContributor.get(
        DependencyToken.STYLES_UPDATE_ROUTER,
      );

      const _setStyles = setStylesRouter(this.setStylesInternal.bind(this));
      return _setStyles(editorPointer, type, styles);
    }

    return this.setStylesInternal(editorPointer, type, styles);
  }

  removeStyles(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    styles: {
      cssPropertiesKeys?: string[];
      cssCustomPropertiesKeys?: string[];
    },
  ) {
    const ROOT_KEY_MAP = {
      cssPropertiesKeys: 'cssProperties',
      cssCustomPropertiesKeys: 'cssCustomProperties',
    };

    const _styles = Object.entries(styles).reduce((acc, [key, values]) => {
      const path = ROOT_KEY_MAP[key as keyof typeof ROOT_KEY_MAP];

      if (!path) {
        return acc;
      }

      if (values?.length) {
        return [...acc, ...values.map((v) => `${path}.${v}`)];
      }

      return acc;
    }, [] as string[]);

    switch (type) {
      case ManifestComponentExtensionType.BUILDER: {
        const removeStyles = this.dependenciesContributor.get(
          DependencyToken.BUILDER_STYLES_REMOVE,
        );

        removeStyles(editorPointer, _styles);

        break;
      }

      case ManifestComponentExtensionType.OOI: {
        throw new Error('not implemented');
      }

      default: {
        const removeStyles = this.dependenciesContributor.get(
          DependencyToken.UNKNOWN_COMPONENT_STYLES_REMOVE,
        );

        return removeStyles(editorPointer, _styles);
      }
    }

    this.manifestChangeDetection.notify({
      type: ChangeType.Styles,
      editorPointer,
      extensionType: type,
      value: {
        cssProperties: toUndefinedObject(styles?.cssPropertiesKeys),
        cssCustomProperties: toUndefinedObject(styles?.cssCustomPropertiesKeys),
      },
    });
  }
}

function toUndefinedObject(keys?: string[]): Record<string, undefined> {
  if (!keys?.length) {
    return {};
  }

  return keys.reduce<Record<string, undefined>>((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {});
}

function resolveNonCssLikeValues(styles: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(styles).map(([key, value]) => [
      key,
      stylesOverrides[value] ?? value,
    ]),
  );
}
